import {
    setBpmUserLogged,
    setCompaniesAssigned,
    setFilterListByCompanies,
    setFilterListByFinancials,
    setFinancialIncAssigned,
    setSourceCodesAssigned,
    setUserPermissions,
    setUserPermissionV2,
} from "@store/slices/sessionSlice";
import { baseApi } from "./baseApi";
import { BpmUser, UserCompanies, UserData } from "@interfaces/users";
import { UUID } from "crypto";
import { Company } from "@interfaces/companies";
import { PaginatedObject, Response, ResponsePagination } from "@interfaces/response";
import { FinancialCompany } from "@interfaces/catalogs";

const usersApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        getCurrentUserInfoV2: build.query<Response<UserData>, string>({
            query: (sso_id) => ({
                url: `usuarios/${sso_id}`,
            }),
            transformResponse: (response: Response<UserData>) => {
                return response;
            },
            async onQueryStarted(arg, api) {
                try {
                    const { data } = await api.queryFulfilled;

                    api.dispatch(setBpmUserLogged(data.data.user_data));
                    api.dispatch(setUserPermissions(data.data.permissions));
                    api.dispatch(setUserPermissionV2(data.data.permissions_v2));
                    api.dispatch(setCompaniesAssigned(data.data.companies));
                    api.dispatch(setSourceCodesAssigned(data.data.source_code));
                    api.dispatch(setFinancialIncAssigned(data.data.financial_company));

                    api.dispatch(setFilterListByCompanies(data.data.companies));
                    api.dispatch(setFilterListByFinancials(data.data.financial_company));
                } catch (error) {}
            },
        }),
        getUsers: build.query<PaginatedObject<BpmUser>, string>({
            query: (params) => ({
                url: `usuarios?${params}`,
            }),
            transformResponse: (response: ResponsePagination<BpmUser>) => {
                return response.data;
            },
        }),
        getUsersResume: build.query<PaginatedObject<BpmUser>, string>({
            query: (params) => ({
                url: `usuarios/resume?${params}`,
            }),
            transformResponse: (response: ResponsePagination<BpmUser>) => {
                return response.data;
            },
        }),
        postUser: build.mutation<BpmUser, any>({
            query: (body) => ({
                url: `usuarios`,
                method: "POST",
                body: body,
            }),
            transformResponse: (response: Response<BpmUser>) => {
                return response.data;
            },
        }),
        patchUser: build.mutation<BpmUser, { userId: UUID; body: Partial<BpmUser> }>({
            query: ({ userId, body }) => ({
                url: `usuarios/${userId}`,
                method: "PATCH",
                body: body,
            }),
        }),
        deleteUser: build.mutation<any, UUID>({
            query: (userId) => ({
                url: `usuarios/${userId}`,
                method: "DELETE",
            }),
        }),
        getUserCompanies: build.query<Array<Company>, UUID>({
            query: (userId) => ({
                url: `usuarios/${userId}/companies`,
            }),
            transformResponse: (response: Response<UserCompanies>) => {
                return response.data.companies;
            },
        }),
        deleteUserCompanies: build.mutation<any, { userId: UUID; companyId: UUID }>({
            query: ({ userId, companyId }) => ({
                url: `usuarios/${userId}/companies/${companyId}`,
                method: "DELETE",
            }),
        }),
        postUserCompany: build.mutation<any, { userId: UUID; company_id: UUID | string }>({
            query: ({ userId, company_id }) => ({
                url: `usuarios/${userId}/companies`,
                method: "POST",
                body: {
                    company_id: company_id,
                },
            }),
        }),
        getUserFinancialCompanies: build.query<Array<FinancialCompany>, UUID>({
            query: (userId) => ({
                url: `usuarios/${userId}/financial_companies`,
            }),
            transformResponse: (response: Response<Array<FinancialCompany>>) => {
                return response.data;
            },
        }),
        postUserFinancialCompany: build.mutation<void, { userId: UUID; financialId: UUID }>({
            query: ({ userId, financialId }) => ({
                url: `usuarios/${userId}/financial_companies`,
                method: "POST",
                body: {
                    financial_company_id: financialId,
                },
            }),
        }),
        deleteUserFinancialCompany: build.mutation<void, { userId: UUID; financialId: UUID }>({
            query: ({ userId, financialId }) => ({
                url: `usuarios/${userId}/financial_companies/${financialId}`,
                method: "DELETE",
            }),
        }),
        deleteUserSession: build.mutation<void, string[]>({
            query: (emails) => ({
                url: `usuarios/signout/masive`,
                method: "POST",
                body: emails,
            }),
        }),
    }),
});

export const {
    useLazyGetCurrentUserInfoV2Query,
    useLazyGetUsersQuery,
    useGetUsersQuery,
    useLazyGetUsersResumeQuery,
    usePostUserMutation,
    usePatchUserMutation,
    useDeleteUserMutation,
    useGetUserCompaniesQuery,
    useLazyGetUserCompaniesQuery,
    useDeleteUserCompaniesMutation,
    usePostUserCompanyMutation,
    useGetUserFinancialCompaniesQuery,
    useLazyGetUserFinancialCompaniesQuery,
    usePostUserFinancialCompanyMutation,
    useDeleteUserFinancialCompanyMutation,
    useDeleteUserSessionMutation,
} = usersApi;
